import React, { useState } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { AddCircle as AddCircleIcon } from '@mui/icons-material';

import CustomSortableTable, { SortableTableAction, SortableTableFilter, SortableTableHeader, SortableTableRow } from '../../../../../../components/CustomSortableTable';

import { StationItem } from '../Common';

import styles from './styles';
import CustomDialog from '../../../../../CustomDialog';
import { booleanToYesNo } from '../../../../../../utils/stringUtils';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
  section_id: string;
  items: StationItem[];
  handleCreate: () => void;
  handleEdit: (id: string) => void;
  handleActiveStatusChange: (id: string, isActive: boolean) => Promise<void>;
}

const ListStations = ({ classes, history, items, section_id, handleCreate, handleEdit, handleActiveStatusChange }: Props): React.ReactElement => {
  const [activeFiltered, setActiveFiltered] = useState<boolean>(true);
  const [recordStatusState, setRecordStatusState] = useState<{
    dialogOpen: boolean;
    stationId: string;
    isActive: boolean;
    isLoading: boolean;
  }>({
    dialogOpen: false,
    stationId: '',
    isActive: false,
    isLoading: false,
  });

  const deactivateStation = async () => {
    setRecordStatusState((prev) => ({ ...prev, isLoading: true }));
    await handleActiveStatusChange(recordStatusState.stationId, recordStatusState.isActive);
    setRecordStatusState((prev) => ({ ...prev, isLoading: false, dialogOpen: false }));
  };

  const headers: SortableTableHeader[] = [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'active', label: 'Active' },
  ];

  const rows: SortableTableRow[] = items.map((station: StationItem) => ({
    key: station.id,
    actions: [
      {
        label: station.active ? 'Deactivate' : 'Activate',
        destructive: station.active,
        onClick: () => setRecordStatusState((prev) => ({ ...prev, stationId: station.id, isActive: !station.active, dialogOpen: true })),
      },
      {
        label: 'Edit',
        onClick: () => handleEdit(station.id),
      },
    ],
    columns: [
      {
        key: 'name',
        label: station.name,
      },
      {
        key: 'phone',
        label: station.phone,
      },
      {
        key: 'active',
        label: booleanToYesNo(station.active),
      },
    ],
  }));

  const actions: SortableTableAction[] = [
    {
      key: 'create',
      label: 'Create bed space',
      icon: <AddCircleIcon />,
      onClick: () => handleCreate(),
    },
  ];

  const activeFilter: SortableTableFilter = {
    key: 'activeFilter',
    label: 'Active',
    filter: (rows: SortableTableRow[]) =>
      rows.filter((r) => {
        const col = r.columns.find((c) => c.key === 'active');
        if (col) {
          return activeFiltered ? col.label === 'Yes' : col.label === 'No';
        }
        return true;
      }),
    component: (
      <ToggleButtonGroup className={classes.activeFilter} size="medium" value={[activeFiltered]} color="primary" onChange={() => setActiveFiltered(!activeFiltered)}>
        <ToggleButton className={classes.toggleButton} key="active" value={true}>
          Active
        </ToggleButton>
        <ToggleButton className={classes.toggleButton} key="inactive" value={false}>
          Inactive
        </ToggleButton>
      </ToggleButtonGroup>
    ),
  };

  const filters = [activeFilter];

  const filterRows = (): SortableTableRow[] => {
    let filteredRows: SortableTableRow[] = rows;
    filters.forEach((f) => {
      filteredRows = f.filter(filteredRows);
    });
    return filteredRows;
  };

  return (
    <>
      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12}>
          <CustomSortableTable title="Bed spaces" orderdBy="name" ordered="asc" actions={actions} headers={headers} counter={rows.length} rows={filterRows()} filters={filters} />
        </Grid>
      </Grid>
      <CustomDialog
        open={recordStatusState.dialogOpen}
        title="Update Station"
        message={`Are you sure you want to ${recordStatusState.isActive ? 'activate' : 'deactivate'} this station?`}
        handleClose={() => setRecordStatusState((prev) => ({ ...prev, dialogOpen: false }))}
        actions={[
          { label: 'No', onClick: () => setRecordStatusState((prev) => ({ ...prev, dialogOpen: false })) },
          { label: 'Yes', onClick: () => deactivateStation() },
        ]}
      />
    </>
  );
};

export default withRouter(withStyles(styles)(ListStations));
